import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, homeFor } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ThemeToggle from "@/components/ThemeToggle";
import VenturaLogo from "@/components/VenturaLogo";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setCode("");
        setUseRecovery(false);
      } else {
        navigate(homeFor(result.user));
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await verify2fa(tempToken, code.trim());
      navigate(homeFor(user));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const volverACredenciales = () => {
    setTempToken(null);
    setCode("");
    setUseRecovery(false);
    setError("");
  };

  return (
    <div className="relative flex min-h-screen bg-white dark:bg-zinc-950">
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <div className="flex w-full flex-col justify-center px-6 sm:px-12 lg:w-[45%] lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10">
            <VenturaLogo />
            <p className="mt-2 text-xs text-zinc-400">Registro de jornada</p>
          </div>

          {!tempToken ? (
            <>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                Bienvenido de nuevo
              </h1>
              <p className="mt-2 text-sm text-zinc-500">Inicia sesión para registrar tu jornada laboral.</p>

              <form onSubmit={onSubmitPassword} className="mt-8 space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="tu@empresa.es"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                    Contraseña
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11"
                  />
                </div>

                {error && (
                  <p data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="h-11 w-full rounded-lg bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  {loading ? "Entrando..." : "Iniciar sesión"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                Verificación en dos pasos
              </h1>
              <p className="mt-2 text-sm text-zinc-500">
                {useRecovery
                  ? "Introduce uno de tus códigos de recuperación."
                  : "Introduce el código de 6 dígitos de tu app autenticadora."}
              </p>

              <form onSubmit={onSubmit2fa} className="mt-8 space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="totp" className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
                    {useRecovery ? "Código de recuperación" : "Código"}
                  </Label>
                  <Input
                    id="totp"
                    required
                    autoComplete="one-time-code"
                    inputMode={useRecovery ? "text" : "numeric"}
                    placeholder={useRecovery ? "XXXXX-XXXXX" : "000000"}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="login-2fa-input"
                    className="h-11 font-mono tracking-widest"
                  />
                </div>

                {error && (
                  <p data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={loading || !code.trim()}
                  data-testid="login-2fa-submit"
                  className="h-11 w-full rounded-lg bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  {loading ? "Verificando..." : "Verificar"}
                </Button>

                <div className="flex flex-col gap-2 text-center text-sm">
                  <button
                    type="button"
                    className="text-zinc-500 hover:text-zinc-800 hover:underline"
                    onClick={() => {
                      setUseRecovery((v) => !v);
                      setCode("");
                      setError("");
                    }}
                  >
                    {useRecovery ? "Usar código de la app" : "Usar código de recuperación"}
                  </button>
                  <button type="button" className="text-zinc-500 hover:text-zinc-800 hover:underline" onClick={volverACredenciales}>
                    Volver
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="relative hidden lg:block lg:w-[55%]">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069"
          alt="Oficina"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-display text-3xl font-semibold tracking-tight leading-snug">
            Tu jornada, registrada.
            <br />
            Tu equipo, de un vistazo.
          </p>
          <p className="mt-3 text-sm text-white/70">
            Fichajes, vacaciones, informes y planificación en un solo lugar.
          </p>
        </div>
      </div>
    </div>
  );
}
